import { PLATFORMS } from '@lib/utils/platforms';
import { X } from '@mui/icons-material';
import type { CSSProperties } from 'react';
import type {
  SocialAccountUnavailable,
  SocialConnector,
  SocialPlatform,
  SocialPlatformModule,
} from '../types';
import {
  facebookModule,
  type FacebookAccountCapabilities,
  type FacebookPostContent,
} from './facebook';
import { tiktokModule, type TikTokAccountCapabilities, type TikTokPostContent } from './tiktok';
import { youtubeModule, type YouTubeAccountCapabilities, type YouTubePostContent } from './youtube';

/** Every shipped platform's exact content. Grows one member per module. */
export type SocialPostContent = TikTokPostContent | FacebookPostContent | YouTubePostContent;

/** Every shipped platform's `available: true` capabilities, discriminated on `platform`. Grows with `SocialPostContent`. */
export type SocialAvailableCapabilities =
  | TikTokAccountCapabilities
  | FacebookAccountCapabilities
  | YouTubeAccountCapabilities;

/**
 * What an account may post right now, read live from the platform
 * (`GET /social/connections/{id}/capabilities`), in the platform's own
 * vocabulary; its module reads it. The other arm is the platform's verdict on
 * the account.
 */
export type SocialAccountCapabilities = SocialAvailableCapabilities | SocialAccountUnavailable;

/** A module of any shipped platform, for code that dispatches on `platform`. */
export type AnySocialPlatformModule = SocialPlatformModule<
  SocialPostContent,
  SocialAvailableCapabilities
>;

export const SOCIAL_PLATFORM_MODULES: {
  [P in SocialPlatform]: SocialPlatformModule<
    Extract<SocialPostContent, { platform: P }>,
    Extract<SocialAvailableCapabilities, { platform: P }>
  >;
} = {
  tiktok: tiktokModule,
  facebook: facebookModule,
  youtube: youtubeModule,
};

/** Undefined for a platform this client does not know: render it as unsupported, never as an error. */
export const getSocialPlatformModule = (platform: string): AnySocialPlatformModule | undefined =>
  Object.prototype.hasOwnProperty.call(SOCIAL_PLATFORM_MODULES, platform)
    ? SOCIAL_PLATFORM_MODULES[platform as SocialPlatform]
    : undefined;

const shippedConnector = (module: AnySocialPlatformModule): SocialConnector => ({
  platform: module.platform,
  label: module.label,
  icon: module.icon,
  colors: module.colors,
  ...(module.connectNote !== undefined && { connectNote: module.connectNote }),
  available: true,
});

/** Shipped modules first, in the order the connect surface lists them, then the upcoming ones. */
export const SOCIAL_CONNECTORS: readonly SocialConnector[] = [
  shippedConnector(tiktokModule),
  shippedConnector(facebookModule),
  shippedConnector(youtubeModule),
  {
    platform: 'instagram',
    label: PLATFORMS.instagram.displayName,
    icon: <img src={PLATFORMS.instagram.icon} alt="" />,
    colors: { bg: 'rgba(225, 48, 108, 0.16)', text: '#ff8ab4' },
    available: false,
  },
  {
    platform: 'linkedin',
    label: PLATFORMS.linkedin.displayName,
    icon: <img src={PLATFORMS.linkedin.icon} alt="" />,
    colors: { bg: 'rgba(10, 102, 194, 0.18)', text: '#8ac0f5' },
    available: false,
  },
  {
    platform: 'x',
    label: 'X',
    icon: <X />,
    colors: { bg: 'rgba(255, 255, 255, 0.1)', text: '#e7e7e7' },
    available: false,
  },
];

/** Undefined for a platform this client does not know: render it as unsupported, never as an error. */
export const getSocialConnector = (platform: string): SocialConnector | undefined =>
  SOCIAL_CONNECTORS.find((c) => c.platform === platform);

/**
 * The inline part of a platform tint: the two custom properties the
 * `platform-tint` SCSS mixin reads. Undefined for an unknown platform, so the
 * mixin's neutral fallback applies.
 */
export const platformTintStyle = (
  connector: SocialConnector | undefined,
): CSSProperties | undefined =>
  connector
    ? ({
        '--platform-bg': connector.colors.bg,
        '--platform-text': connector.colors.text,
      } as CSSProperties)
    : undefined;
